import React, { useState } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEmployeeSurveyResponses } from '../../hooks';
import CommutingSection from './CommutingSection';
import HomeOfficeSection from './HomeOfficeSection';
import BusinessTripsSection from './BusinessTripsSection';
import StaysSection from './StaysSection';
import SurveyInfoSection from './SurveyInfoSection';
import { EmployeeSurveyResponseData } from './types';
import ConfirmationModal from 'src/components/ConfirmationModal';
import { useNavigate } from 'react-router-dom';
import { useGhgOrganization } from 'src/common/hooks';

interface Props {
  responseId?: number;
}

export default function Form({ responseId }: Props) {
  const { t: tGhg } = useTranslation('ghg');
  const navigate = useNavigate();
  const organization = useGhgOrganization();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const {
    _instance: response,
    isLoading,
    error,
    update,
    _delete,
  } = useEmployeeSurveyResponses({ id: responseId });

  const [surveyData, setSurveyData] =
    React.useState<EmployeeSurveyResponseData>({
      commute: [],
      average_number_of_days_on_home_office_per_week: 0,
      business_trips: [],
      stays: [],
    });

  const initialData = response?.data_parsed ||
    response?.data_raw || {
      commute: [],
      average_number_of_days_on_home_office_per_week: 0,
      business_trips: [],
      stays: [],
    };
  React.useEffect(() => {
    setSurveyData(initialData);
  }, [initialData]);

  const handleDelete = () => {
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = () => {
    _delete.mutateAsync(Number(responseId)).then(() => {
      navigate(-1);
    });
    setShowDeleteConfirmation(false);
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>
          {tGhg('employeeSurveys.previewTable.states.loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">
          {tGhg('employeeSurveys.previewTable.states.error', {
            message: error.message,
          })}
        </Typography>
      </Box>
    );
  }

  if (!response) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {tGhg('employeeSurveys.previewTable.states.empty')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000 }}>
      <SurveyInfoSection
        organizationName={response.organization_name}
        period={response.period}
        createdAt={response.created_at}
        withoutExtrapolation={response.without_extrapolation}
        canDelete={organization.can_write_data}
        onDeleteclick={handleDelete}
      />

      <ConfirmationModal
        open={showDeleteConfirmation}
        titleKey="ghg:employeeSurveys.detail.deleteConfirmation.title"
        textKey="ghg:employeeSurveys.detail.deleteConfirmation.text"
        trueButtonTextKey="ghg:employeeSurveys.detail.delete"
        falseButtonTextKey="common.cancel"
        onTrue={confirmDelete}
        onFalse={cancelDelete}
        onClose={cancelDelete}
      />

      <br />
      <br />
      <CommutingSection
        onSave={() =>
          update.mutateAsync({
            ...response,
            data_raw: {
              ...response.data_raw,
              commute: surveyData.commute,
            },
          })
        }
        commute={surveyData.commute}
        onChange={(commute) =>
          setSurveyData((prevState) => ({ ...prevState, commute }))
        }
      />
      <Divider sx={{ my: 4 }} />
      <HomeOfficeSection
        onSave={() =>
          update.mutateAsync({
            ...response,
            data_raw: {
              ...response.data_raw,
              average_number_of_days_on_home_office_per_week:
                surveyData.average_number_of_days_on_home_office_per_week,
            },
          })
        }
        averageDaysPerWeek={
          surveyData.average_number_of_days_on_home_office_per_week
        }
        onChange={(averageDaysPerWeek) =>
          setSurveyData((prevState) => ({
            ...prevState,
            average_number_of_days_on_home_office_per_week:
              Number(averageDaysPerWeek),
          }))
        }
      />
      <Divider sx={{ my: 4 }} />
      <BusinessTripsSection
        data={surveyData.business_trips}
        onChange={(business_trips) =>
          setSurveyData((prevState) => ({ ...prevState, business_trips }))
        }
        onSave={() =>
          update.mutateAsync({
            ...response,
            data_raw: {
              ...response.data_raw,
              business_trips: surveyData.business_trips,
            },
          })
        }
      />
      <Divider sx={{ my: 4 }} />
      <StaysSection
        data={surveyData.stays}
        onChange={(stays) =>
          setSurveyData((prevState) => ({ ...prevState, stays }))
        }
        onSave={() =>
          update.mutateAsync({
            ...response,
            data_raw: {
              ...response.data_raw,
              stays: surveyData.stays,
            },
          })
        }
      />
    </Box>
  );
}
